import React, { ForwardRefRenderFunction, forwardRef, useRef } from "react";
//#region types
import { FormHandles } from "models/propTypes/genericFormProps";
import { TranslationKeys } from "translations";
import { FileConstants, IconDefinitionConstants } from "utils/constants";
import { CommentsFormTypes } from "models/formTypes";
import { ChatAnswerFormProps } from "models/propTypes/commentViewPropTypes";
import { FileUploaderProps, TextInputProps } from "models/propTypes/wintFormPropsTypes";
import { FileEnums } from "models/enums";
//#endregion
//#region components
import { Icon, StyledButton } from "components/common";
import { FileUploader, TextAreaInput, VerticalFormField, WintForm, useWintForm } from "components/common/wintForms";
import { Grid, InputAdornment } from "@mui/material";
//#endregion
import { LocalizationHelperInstance, UtilsHelper } from "utils/helpers";
import cssExports from "theme/styles/main.module.scss";
import clsx from "clsx";

const ChatAnswerForm: ForwardRefRenderFunction<FormHandles, ChatAnswerFormProps> = (props, ref) => {
  const [showAttachments, setShowAttachments] = React.useState<boolean>(false);
  const formBottomRef = useRef<HTMLDivElement>(null);

  const { wintFormProps, connect, values } = useWintForm<CommentsFormTypes.CommentWithFile>({
    formRef: ref,
    defaultValues: props.defaultValues,
    validationSchema: props.validationSchema,
    handleSubmit: props.handleSubmit,
    onFormValidationChange: props.onFormValidationChange,
    onFormValidationError: props.onFormValidationError,
    onFormUpdate: props.onFormUpdate,
  });

  const attachmentsHidden =
    props.attachmentsHiddenCondition === "always" ||
    (props.attachmentsHiddenCondition === "untilHasMessage" && UtilsHelper.isEmpty(values.explanation));

  const onAttachmentClick = () => {
    setShowAttachments(!showAttachments);

    if (!showAttachments && !props.keepScrollOnAttachmentsOpen) {
      setTimeout(() => {
        formBottomRef.current?.scrollIntoView({ behavior: "smooth" });
      }, 100); //TODO find better triggers than time
    }
  };

  return (
    <WintForm {...wintFormProps}>
      <Grid container marginBottom={-1.5} marginTop={-1.5}>
        <VerticalFormField<TextInputProps>
          Component={TextAreaInput}
          {...connect("explanation")}
          // Blur marks the field touched, which shows the required error on a box the user only clicked into.
          onBlur={props.hideErrorsUntilSubmit ? () => {} : connect("explanation").onBlur}
          label={props.answerLabel ?? LocalizationHelperInstance.getFormattedMessage(TranslationKeys.Common.Answer)}
          icon={IconDefinitionConstants.WCommentDots}
          hideMessageBubble
          minRows={props.answerMinRows ?? 1}
          maxRows={5}
          endAdornment={
            attachmentsHidden ? undefined : (
              <InputAdornment position="end" className={cssExports.inputAdornedEnd}>
                <StyledButton
                  size={"xs"}
                  className={clsx(
                    cssExports.iconButton,
                    cssExports.withActiveState,
                    cssExports.transparent,
                    showAttachments ? cssExports.active : undefined
                  )}
                  onClick={onAttachmentClick}
                >
                  <Icon icon={IconDefinitionConstants.WAttachment} />
                </StyledButton>
              </InputAdornment>
            )
          }
        />
      </Grid>
      <Grid container marginBottom={-1} marginTop={0.5} ref={formBottomRef}>
        {showAttachments ? (
          <VerticalFormField<FileUploaderProps>
            Component={FileUploader}
            allowedTypes={props.attachmentTypes ?? [FileEnums.ContentTypes.ApplicationPDF, FileEnums.ContentTypes.CSV]}
            allowAnyImageTypes
            {...connect("filesToUpload")}
            filePreviewElementId={FileConstants.DialogFilePreviewPortalId} //TODO should the file preview be shown?
          />
        ) : null}
      </Grid>
    </WintForm>
  );
};

export default forwardRef(ChatAnswerForm);
